import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { API, errorMessage, fetchOtpConfig } from "../../lib/api";
import { Field, inputCls } from "./Field";

const sanitize = (v) => v.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "").slice(0, 10);

export const PhoneVerify = ({ prefix, label, phone, onPhone, token, onToken }) => {
  const [enabled, setEnabled] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    fetchOtpConfig().then((c) => setEnabled(c.enabled)).catch(() => setEnabled(false));
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const changePhone = (v) => { onPhone(sanitize(v)); setSent(false); setCode(""); setCooldown(0); };

  const call = async (path, body, onOk) => {
    setBusy(true);
    try { onOk((await axios.post(`${API}/otp/${path}`, body)).data); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const send = () => {
    if (phone.length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    return call("send", { phone }, () => { setSent(true); setCooldown(30); toast.success(`OTP sent to +91 ${phone}`); });
  };
  const verify = () => call("verify", { phone, code }, (d) => { onToken(d.verification_token); toast.success("Mobile number verified"); });

  const verified = Boolean(token);
  return (
    <Field label={label} hint={enabled ? "OTP verification required" : undefined}>
      <div className="flex">
        <span className="flex h-10 items-center rounded-l-lg border border-r-0 border-white/15 bg-[#1E293B] px-3 font-mono text-sm text-slate-400">+91</span>
        <Input required inputMode="numeric" data-testid={`${prefix}-input-phone`} className={`${inputCls} rounded-none ${enabled && !verified ? "" : "rounded-r-lg"}`} value={phone}
          onChange={(e) => changePhone(e.target.value)} placeholder="10-digit mobile" />
        {enabled && !verified && (
          <button type="button" onClick={send} disabled={busy || cooldown > 0} data-testid={`${prefix}-otp-send-button`}
            className="h-10 shrink-0 whitespace-nowrap rounded-r-lg border border-l-0 border-orange-500/60 px-4 text-sm font-semibold text-orange-300 transition-colors hover:bg-orange-500 hover:text-white disabled:opacity-50">
            {cooldown > 0 ? `Resend ${cooldown}s` : sent ? "Resend" : "Send OTP"}
          </button>
        )}
      </div>
      {verified && (
        <p data-testid={`${prefix}-otp-verified`} className="flex items-center gap-1.5 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" />Verified</p>
      )}
      {enabled && sent && !verified && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <InputOTP maxLength={6} value={code} onChange={setCode} data-testid={`${prefix}-otp-input`}>
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-10 w-9 border-white/15 font-mono text-white" />)}
            </InputOTPGroup>
          </InputOTP>
          <button type="button" onClick={verify} disabled={busy || code.length < 4} data-testid={`${prefix}-otp-verify-button`}
            className="flex h-10 items-center gap-2 bg-orange-500 px-4 text-sm font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Verify
          </button>
        </div>
      )}
    </Field>
  );
};
